import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import ToastService from "../../../../../../core/services/toast";
import Utils from "../../../../../../core/services/utils/utils";
import {Box, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import LoadingLottieAnimation from "../../../../app-specific/lottie-animations/loading";
import ImportCsvFile, {ImportCsvFileSteps} from "../../../../app-specific/import-csv-file";
import {ArrowBack} from "@mui/icons-material";
import IconButton from "../../../../base/button/icon_button";
import Button from "../../../../base/button/button";
import {ImportAdjustmentSteps} from "../../index";
import {validateAdjustmentImport} from "../../engine/validate-adjustment-import";

const initialCsvFileState = {
    step: ImportCsvFileSteps.dropzone,
    parsedColumns: [],
    parsedRows: [],
    csvEntry: null,
}

const ImportSection = ({
                           setReviewSectionState,
                           setStatus,
                           loading,
                           fields,
                       }) => {
    const formId = useMemo(() => Utils.createUUID(), []);
    const [importCsvFileState, setImportCsvFileState] = useState(initialCsvFileState);
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Callback function passed to the column mapper
     * @type {(function({rows: *, columns: *, csvEntry: *}): void)}
     */
    const onDropzoneParsed = useCallback(({rows, columns, csvEntry}) => {
        setImportCsvFileState({
            step: ImportCsvFileSteps.columnMapper,
            parsedColumns: columns,
            parsedRows: rows,
            csvEntry: csvEntry,
        });
    }, [])

    /**
     * Callback function passed to the column mapper. Validates the items and sets the review section state.
     * @type {(function(*): Promise<void>)}
     */
    const onColumnMapperParsed = useCallback(async (parsedRows) => {
        setImporting(true);
        const validationResult = await validateAdjustmentImport(parsedRows);
        if (!isMounted()) {
            return;
        }
        setImporting(false);

        if (!validationResult) {
            ToastService.error({
                title: "Error",
                message: "Unable to validate the imported adjustments. Please check your file and try again.",
            });
            setImportCsvFileState(initialCsvFileState);
            return;
        }

        const {
            parsedRows: rows,
            preventativeIssues,
            addItems,
            removeItems,
            invalidEntries,
            validEntries,
        } = validationResult;

        // Always proceed to review so operators can fix preventative issues in-dialog.
        setReviewSectionState({
            validEntries: validEntries ?? [],
            invalidEntries: invalidEntries ?? [],
            addItems: addItems ?? [],
            removeItems: removeItems ?? [],
            parsedRows: rows ?? [],
            preventativeIssues: preventativeIssues ?? [],
            userSkippedRowIndices: [],
            addReasonAndNote: {
                reason: null,
                note: '',
            },
            removeReasonAndNote: {
                reason: null,
                note: '',
            },
        });
        setStatus(ImportAdjustmentSteps.reviewSection);
    }, [isMounted, setReviewSectionState, setStatus])

    return (
        <>
            <DialogTitle id={'scroll-dialog-title'} component={'div'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    Import Adjustments
                </p>
            </DialogTitle>
            <DialogContent
                className={'p-3 d-flex flex-column align-items-center w-100'}
                dividers
            >
                {
                    (loading || importing) &&
                    <LoadingLottieAnimation
                        width={'80%'}
                        className={'my-5'}
                    />
                }
                {
                    !(loading || importing) &&
                    <>
                        <div className={'d-flex flex-column'}>
                            {
                                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                                <>
                                    <div className={'d-flex align-items-center'}>
                                        <IconButton
                                            onPressed={() => setImportCsvFileState(initialCsvFileState)}
                                            color={'paper.secondary'}
                                            className={'mr-2'}
                                        >
                                            <ArrowBack className={'icon normal'}/>
                                        </IconButton>
                                        <Typography variant={'body1'} fontWeight={600}>
                                            Column Mapping
                                        </Typography>
                                    </div>
                                    <Typography variant={'body2'} className={'mt-2 mb-4'}>
                                        Please map the columns to the fields so we can process the adjustments.
                                    </Typography>
                                </>
                            }
                            <ImportCsvFile
                                step={importCsvFileState.step}
                                fields={fields}
                                sampleFileTitle={"inventory-adjustment-sample"}
                                parsedColumns={importCsvFileState.parsedColumns}
                                parsedRows={importCsvFileState.parsedRows}
                                onDropzoneParsed={onDropzoneParsed}
                                onColumnMapperParsed={onColumnMapperParsed}
                                formId={formId}
                                dropzoneNotes={
                                    <div className={'d-flex flex-column my-1'}>
                                        <p className={'placeholder-text'}>
                                            Note that by default, if the cost is "null", the system will ignore the cost field. Also the sample file
                                            contains the following columns:
                                            <br style={{lineHeight: '2rem'}}/>
                                        </p>
                                        <p className={'placeholder-text'}>
                                            If your product is serialized, then you should enter the serial numbers separated by commas in the serial numbers column.
                                            <br style={{lineHeight: '2rem'}}/>
                                        </p>
                                    </div>
                                }
                            />
                        </div>
                    </>
                }
            </DialogContent>
            {
                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => setImportCsvFileState(initialCsvFileState)}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            type={'submit'}
                            form={formId}
                            borderRadius={'8px 0 8px 0'}
                        >
                            Proceed
                        </Button>
                    </Box>
                </DialogActions>
            }
        </>
    );
}

export default ImportSection;
